import { __read, __spreadArray } from "tslib";
import { convertActivityTypeToActivityReason, convertRecentActivities, } from '@office-start/activity-utility';
import { ActivityReason, AttachmentType, FileType, } from '@office-start/document-types';
import { isMsftDogfood, oneDriveConsumerUrlRegex, } from '@office-start/file-utility';
import { logDebugCacheEvent, logGenericEvent, } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { IDLE_LOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { logDiagnostic, logError, logFeature, } from '@office-start/telemetry-service';
import { appendLoginHint, getLocation } from '@office-start/url-parser-utility';
import { RaasLoopPageContainerTypeId, safeLinksRegex, } from '../models/Constants';
import { getFileType, isEmailAttachment } from './FileUtilities';
import { getOdspShareInfo } from './odspShareUtilities';
import { getApplicationFromType, getMeetingObjects, getPathArray, isOneDriveForBusinessFile as isOneDriveForBusinessFileFn, isSharePointFile as isSharePointFileFn, } from './ParseUtilities';
/**
 * Migrated from toMruFileItems() in edgeworth.component.ts
 * @param _options object of type ParseEdgeworthDataProps
 * @returns An array of parsed Edgeworth documents
 */
export function parseEdgeworthData(_a) {
    var fileItems = _a.fileItems, untitledFileNameLocString = _a.untitledFileNameLocString, filePathTitleLocString = _a.filePathTitleLocString, userPrincipalName = _a.userPrincipalName, meetingObjectsMap = _a.meetingObjectsMap, filterCallback = _a.filterCallback, outgoingLoginHintEnabled = _a.outgoingLoginHintEnabled, loginHint = _a.loginHint, msaUserHint = _a.msaUserHint, editActivityLocString = _a.editActivityLocString, sharedActivityLocString = _a.sharedActivityLocString, sentAttachmentActivityLocString = _a.sentAttachmentActivityLocString, oneDrivePathTitleLocString = _a.oneDrivePathTitleLocString, unknownUserTextLocString = _a.unknownUserTextLocString, yourOneDrivePathTitleLocString = _a.yourOneDrivePathTitleLocString, isRtl = _a.isRtl, loopRaasPagesFixEnabled = _a.loopRaasPagesFixEnabled, draftEnabled = _a.draftEnabled;
    var itemsWithMissingUrls = [];
    var parsedItems = fileItems
        .map(function (item) {
        var _a;
        var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        if (!item || (!!filterCallback && !filterCallback(item))) {
            return null;
        }
        try {
            var isOneDriveForBusinessFile = isOneDriveForBusinessFileFn(item.source);
            var isSharePointFile = isSharePointFileFn(item.source);
            var driveId = (_b = item.onedrive_info) === null || _b === void 0 ? void 0 : _b.drive_id;
            var isEmailAttachmentFile = isEmailAttachment(item, !!msaUserHint);
            var isLocalFile = item.source === 'LocalFile';
            var itemId = item.id;
            var docId = ((_d = (_c = item.onedrive_info) === null || _c === void 0 ? void 0 : _c.item_id) === null || _d === void 0 ? void 0 : _d.toString()) || '';
            var extension = item.extension;
            var fileType = getFileType(item, draftEnabled);
            var url = item.url;
            var webUrl = item.web_url || item.url;
            if (isEmailAttachmentFile) {
                webUrl = item.web_url;
                if (!url) {
                    url = item.web_url;
                }
            }
            var creatorName = (_f = (_e = item.creation_info) === null || _e === void 0 ? void 0 : _e.user) === null || _f === void 0 ? void 0 : _f.display_name;
            var lastActionTime = item.user_relationship.last_action_datetime;
            var userRelationship = item.user_relationship;
            var lastAccessTime = userRelationship.last_access_datetime;
            var sitePath = item.sharepoint_info
                ? item.sharepoint_info.site_path
                : '';
            var lastActivity = item.activities
                ? item.activities[0]
                : null;
            var isConsumerFileOwner = !!msaUserHint &&
                msaUserHint.toLowerCase() === (driveId === null || driveId === void 0 ? void 0 : driveId.toLowerCase());
            // If consumer user and current file does not belong to user, then use creatorName as container title
            var containerTitle = !!msaUserHint && !isConsumerFileOwner
                ? creatorName
                : item.container_title;
            var pathArray = getPathArray({
                filePathTitleLocString: filePathTitleLocString,
                isConsumerFileOwner: isConsumerFileOwner,
                isLocalFile: isLocalFile,
                isOneDriveForBusinessFile: isOneDriveForBusinessFile,
                isSharePointFile: isSharePointFile,
                containerTitle: containerTitle,
                oneDrivePathTitleLocString: oneDrivePathTitleLocString,
                sitePath: sitePath,
                webUrl: webUrl,
                url: url,
                yourOneDrivePathTitleLocString: yourOneDrivePathTitleLocString,
            });
            var attribution = '';
            if (pathArray.length) {
                if (msaUserHint && pathArray.length > 1) {
                    var tempPathArray = isRtl
                        ? __spreadArray([], __read(pathArray), false).reverse()
                        : pathArray;
                    attribution = tempPathArray.join(" ".concat(isRtl ? '<' : '>', " "));
                }
                else {
                    attribution = pathArray[0];
                }
            }
            var hasUnseenActivity = function () {
                if (item.activities &&
                    item.user_relationship &&
                    item.user_relationship.last_access_datetime) {
                    var mostRecentActivityFromOtherActor = item.activities.find(function (activityCandidate) {
                        var _a;
                        return ((_a = activityCandidate.da) === null || _a === void 0 ? void 0 : _a.e) !==
                            userPrincipalName;
                    });
                    return ((mostRecentActivityFromOtherActor === null || mostRecentActivityFromOtherActor === void 0 ? void 0 : mostRecentActivityFromOtherActor.t) &&
                        mostRecentActivityFromOtherActor.t >
                            item.user_relationship.last_access_datetime);
                }
                return false;
            };
            var sharedByEmail = void 0, sharedById_1, sharedByFullUsername = (_g = item.user_relationship) === null || _g === void 0 ? void 0 : _g.last_shared_display_name;
            var lastSharedTime = (_h = item.user_relationship) === null || _h === void 0 ? void 0 : _h.last_shared_datetime;
            if (msaUserHint &&
                !isConsumerFileOwner &&
                !lastSharedTime &&
                driveId //For files like Forms, Sway etc
            ) {
                lastSharedTime = lastActionTime;
            }
            if (item.user_relationship &&
                sharedByFullUsername &&
                item.user_relationship.last_shared_by_id) {
                sharedById_1 = item.user_relationship.last_shared_by_id;
                if (item.user_relationship.last_shared_smtp) {
                    sharedByEmail = item.user_relationship.last_shared_smtp;
                }
                else if (item.user_relationship.last_shared_by_history) {
                    sharedByEmail =
                        item.user_relationship.last_shared_by_history[0];
                }
                else if (!!item.activities) {
                    //Try to find the sharer in activities
                    var matchedActivity = item.activities.find(function (activityElement) {
                        return activityElement.da.i === sharedById_1;
                    });
                    sharedByEmail = matchedActivity === null || matchedActivity === void 0 ? void 0 : matchedActivity.da.e;
                }
            }
            else if (msaUserHint && lastSharedTime) {
                // This block is for OneDrive Consumer documents shared directly to user
                // Email attachments should have been captured above
                sharedById_1 = driveId;
                sharedByFullUsername = sharedByFullUsername || creatorName;
            }
            var sharedByUsername = sharedByFullUsername;
            var activityDescription = undefined;
            var activityBadge = item
                .activity_badge;
            if (activityBadge && activityBadge.message_format) {
                var plainText_1 = activityBadge.message_format;
                var formatString = activityBadge.message_format;
                var people_1 = [];
                if (activityBadge.users) {
                    activityBadge.users.forEach(function (user, index) {
                        var username = (user === null || user === void 0 ? void 0 : user.display_name) || unknownUserTextLocString;
                        var templateString = "{".concat(index, "}");
                        var tag = index.toString();
                        plainText_1 = plainText_1.replace(templateString, username);
                        people_1.push({
                            displayName: (user === null || user === void 0 ? void 0 : user.display_name) ||
                                unknownUserTextLocString,
                            name: username,
                            tag: tag,
                            upn: (user === null || user === void 0 ? void 0 : user.upn) || '',
                        });
                    });
                }
                activityDescription = {
                    formatString: formatString,
                    plainText: plainText_1,
                    people: people_1,
                    reasonType: convertActivityTypeToActivityReason(activityBadge.type),
                    timestamp: activityBadge.timestamp,
                    id: '0', // TODO: Not sure if this is needed
                };
            }
            else if (msaUserHint && lastSharedTime) {
                if (url.match(oneDriveConsumerUrlRegex)) {
                    var modificationInfoTimestamp = (_j = item.modification_info) === null || _j === void 0 ? void 0 : _j.timestamp;
                    var modificationInfoUserName = (_l = (_k = item.modification_info) === null || _k === void 0 ? void 0 : _k.user) === null || _l === void 0 ? void 0 : _l.display_name;
                    var isEditActivity = !!modificationInfoUserName &&
                        modificationInfoTimestamp &&
                        modificationInfoTimestamp > lastSharedTime;
                    if (isEditActivity) {
                        var activityPerson = {
                            name: modificationInfoUserName || '',
                            upn: '',
                        };
                        activityDescription = {
                            formatString: editActivityLocString,
                            plainText: editActivityLocString.replace('{0}', activityPerson.name),
                            people: [activityPerson],
                            reasonType: ActivityReason.Edit,
                            timestamp: modificationInfoTimestamp,
                            id: '',
                        };
                    }
                    else {
                        var activityPerson = {
                            name: creatorName || '',
                            upn: '',
                        };
                        activityDescription = {
                            formatString: sharedActivityLocString,
                            plainText: sharedActivityLocString.replace('{0}', activityPerson.name),
                            people: [activityPerson],
                            reasonType: ActivityReason.Share,
                            timestamp: lastSharedTime,
                            id: '',
                        };
                    }
                }
                else {
                    var activityPerson = {
                        name: sharedById_1 || '',
                        upn: sharedById_1 || '',
                    };
                    activityDescription = {
                        formatString: sentAttachmentActivityLocString,
                        plainText: sentAttachmentActivityLocString.replace('{0}', activityPerson.name),
                        people: [activityPerson],
                        reasonType: ActivityReason.Attachment,
                        timestamp: lastSharedTime,
                        id: '',
                    };
                }
            }
            // TODO: Safelinks might not be returned from EW APIs anymore
            var isSafeLink = safeLinksRegex.test(url);
            var fileName = item.title;
            // Handle meeting objects
            var meetingObjects = void 0;
            if (meetingObjectsMap && fileType === FileType.MeetingObject) {
                if (!item.subject) {
                    //If the subject for a meeting object is empty, the meeting has been deleted or cancelled
                    return null;
                }
                if (!meetingObjectsMap.has(item.id) &&
                    (item.reference_type === 'CalendarEvent' ||
                        item.reference_type === 'Teams')) {
                    meetingObjectsMap.set(item.id, {
                        subject: item.subject,
                        meetingArtifacts: [],
                    });
                }
                var existingMeetingObjects = [];
                if (meetingObjectsMap.has(item.id)) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    var meetingObject = meetingObjectsMap.get(item.id);
                    fileName = meetingObject === null || meetingObject === void 0 ? void 0 : meetingObject.subject;
                    existingMeetingObjects =
                        meetingObject === null || meetingObject === void 0 ? void 0 : meetingObject.meetingArtifacts;
                    //Merge existing meeting objects with new meeting objects and update map
                    if (item.meeting_artifacts) {
                        meetingObjectsMap.set(item.id, {
                            subject: item.subject,
                            meetingArtifacts: getMeetingObjects(existingMeetingObjects, item.meeting_artifacts, item.created_datetime),
                        });
                    }
                }
                if (item.meeting_artifacts) {
                    meetingObjects = getMeetingObjects(undefined, item.meeting_artifacts, item.created_datetime);
                    if (meetingObjects.length === 0) {
                        logGenericEvent(KnownInstrumentationEvents.MeetingObjectsNoArtifactsFoundError);
                        // TODO: Remove above Aria logging after fully migrating to OTEL
                        logError({
                            eventName: 'Meeting_Objects_No_Artifacts_Found',
                            errorType: 'Meeting_Objects_No_Artifacts_Found_Error',
                        });
                        return undefined;
                    }
                }
            }
            fileName = fileName || untitledFileNameLocString;
            var attributionUrl = void 0;
            if (item.container_url &&
                item.sharing_reference_url &&
                item.container_url.indexOf('/') === 0) {
                // use sharing_reference_url when container_url is relative (eg. email attachments)
                attributionUrl = item.sharing_reference_url;
            }
            else {
                attributionUrl =
                    item.container_url || item.sharing_reference_url;
            }
            // TODO: Move to separate utility function
            if (!attributionUrl &&
                // For consumer, only set attribution URL if file belongs to user and is not an email attachment
                (!msaUserHint || (!item.subject && isConsumerFileOwner)) &&
                fileType !== FileType.Sway &&
                fileType !== FileType.Forms &&
                fileType !== FileType.Project &&
                fileType !== FileType.ProjectOperations) {
                var lastPathSeparator = item.url
                    ? item.url.lastIndexOf(isLocalFile ? '\\' : '/')
                    : -1;
                attributionUrl =
                    lastPathSeparator === -1
                        ? item.url
                        : item.url.substring(0, lastPathSeparator + 1);
                // Must add id=root to folder path for files at root of ODC drive
                if (attributionUrl &&
                    attributionUrl.match(oneDriveConsumerUrlRegex)) {
                    var attributionPath = (_m = getLocation(attributionUrl)) === null || _m === void 0 ? void 0 : _m.path;
                    if (attributionPath &&
                        attributionPath.lastIndexOf('/', attributionPath.length - 2) <= 0) {
                        attributionUrl = "".concat(attributionUrl, "?id=root");
                    }
                }
            }
            // Append login hint to attribution URLs if not a local file
            if (!isLocalFile) {
                attributionUrl =
                    loginHint && attributionUrl
                        ? appendLoginHint(attributionUrl, loginHint, !!outgoingLoginHintEnabled)
                        : attributionUrl;
            }
            // Defaults to true for undefined, as older files synced to users' device will not have this property populated
            var isRedeemed = (_o = item.is_redeemed) !== null && _o !== void 0 ? _o : true;
            var sharepointIds = item.sharepoint_info
                ? {
                    listId: item.sharepoint_info.list_id || '',
                    listItemId: '',
                    listItemUniqueId: item.sharepoint_info.unique_id || '',
                    siteId: item.sharepoint_info.site_id || '',
                    siteUrl: item.sharepoint_info.site_path || '',
                    webId: item.sharepoint_info.web_id || '',
                }
                : undefined;
            var isRaasLoopPage = item.container_type_id === RaasLoopPageContainerTypeId;
            // The attribution url (workspace container) for Loop Raas Pages is broken, so disable until resolved
            if (isRaasLoopPage && loopRaasPagesFixEnabled) {
                attributionUrl = undefined;
            }
            var title = fileName;
            var extensionLastIndex = fileName.lastIndexOf(".".concat(extension));
            if (extensionLastIndex >= 0) {
                title = fileName.substring(0, extensionLastIndex);
            }
            var document_1 = {
                id: itemId,
                impressionId: 'EdgeworthImpressionId',
                author: item.author,
                attribution: attribution,
                attributionUrl: attributionUrl,
                creatorName: creatorName,
                creatorUpn: (_q = (_p = item.creation_info) === null || _p === void 0 ? void 0 : _p.user) === null || _q === void 0 ? void 0 : _q.upn,
                docId: docId,
                driveId: driveId,
                fileName: fileName,
                extension: extension,
                title: title,
                url: url,
                webUrl: webUrl,
                containerTitle: containerTitle,
                canonicalUrl: url || webUrl,
                downloadUrl: url,
                lastAccessedTime: lastAccessTime,
                fileType: fileType,
                pathArray: pathArray,
                app: getApplicationFromType(item),
                isPinned: item.is_pinned ||
                    !!item.user_relationship.save_for_later_datetime,
                lastModifiedBy: lastActivity
                    ? {
                        email: (_r = lastActivity.da) === null || _r === void 0 ? void 0 : _r.e,
                        fullName: (_s = lastActivity.da) === null || _s === void 0 ? void 0 : _s.n,
                    }
                    : undefined,
                activity: activityDescription,
                iconUrl: item.icon_url,
                isCollab: !!item.is_collab,
                isDeleted: !!item.is_deleted,
                isEmailAttachment: isEmailAttachmentFile,
                isHidden: item.is_hidden,
                isMeetingRecording: !!item.is_meeting_recording,
                isMsftDogfood: isMsftDogfood(webUrl),
                isLocalFile: isLocalFile,
                isOneDriveForBusinessFile: isOneDriveForBusinessFile,
                isSharePointFile: isSharePointFile,
                actionInfo: {
                    odspShareInfo: getOdspShareInfo({
                        isRedeemed: isRedeemed,
                        item: item,
                        sharepointIds: sharepointIds,
                        source: item.source,
                        url: url,
                        webUrl: webUrl,
                    }),
                    shareActionInfo: {
                        disabled: isRaasLoopPage && loopRaasPagesFixEnabled,
                    },
                    downloadActionInfo: {
                        disabled: isRaasLoopPage && loopRaasPagesFixEnabled,
                    },
                    openFileLocationActionInfo: {
                        disabled: isRaasLoopPage && loopRaasPagesFixEnabled,
                    },
                },
                createdDate: item.created_datetime,
                sharepointIds: sharepointIds,
                isShared: !!userRelationship.last_shared_by_id,
                isNew: hasUnseenActivity(),
                isRedeemed: isRedeemed,
                isSafeLink: isSafeLink,
                emailReference: !!item.subject
                    ? {
                        AttachmentType: isEmailAttachmentFile
                            ? AttachmentType.Classic
                            : AttachmentType.Modern,
                        ConversationId: '',
                        Subject: item.subject,
                    }
                    : undefined,
                resourceId: item.resource_id,
                sharedByUsername: sharedByUsername,
                sharedByFullUsername: sharedByFullUsername,
                sharedByEmail: sharedByEmail,
                sharedById: sharedById_1,
                lastSharedTime: lastSharedTime,
                lastActionTime: lastActionTime,
                meetingObjects: meetingObjects,
                savedForLaterDateTime: (_t = item.user_relationship) === null || _t === void 0 ? void 0 : _t.save_for_later_datetime,
                recentActivities: item.activities
                    ? convertRecentActivities(item.activities)
                    : [],
                // TODO: Reenable following line only after sci_id property returns new id
                // sciId: item.sci_id,
                mruId: item.mru_id,
                mruType: item.mru_type,
                mruUrl: item.mru_url,
                workingSetId: item.wset_id,
                substrateInfo: item.content_domain_instance_id
                    ? {
                        contentDomain: item.type,
                        contentDomainInstanceId: item.content_domain_instance_id,
                    }
                    : undefined,
            };
            if (!document_1.url && !document_1.webUrl) {
                itemsWithMissingUrls.push(document_1);
            }
            return document_1;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (ex) {
            var params = {};
            params["Error" /* InstrumentationParam.Error */] = ex === null || ex === void 0 ? void 0 : ex.toString();
            logDebugCacheEvent(KnownInstrumentationEvents.EdgeworthParseError, params);
            // TODO: Remove above Aria logging after fully migrating to OTEL
            logDiagnostic({
                eventName: "Edgeworth_Parse_Error" /* KnownDiagnosticInstrumentationEvents.EdgeworthParseError */,
                params: (_a = {}, _a['Error'] = ex === null || ex === void 0 ? void 0 : ex.toString(), _a),
            });
            return null;
        }
    })
        .filter(function (item) { return !!item; });
    if (itemsWithMissingUrls.length) {
        scheduleWork(function () {
            var _a;
            logFeature({
                featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                eventName: "Edgeworth_Parse_Error" /* KnownFeatureInstrumentationEvents.EdgeworthParseError */,
                params: (_a = {},
                    _a["ItemCount" /* InstrumentationParam.ItemCount */] = itemsWithMissingUrls.length,
                    _a["Error" /* InstrumentationParam.Error */] = 'missing webUrl and url',
                    _a),
            });
        }, IDLE_LOW_PRIORITY);
    }
    return parsedItems;
}
//# sourceMappingURL=parseEdgeworthData.js.map